import React, { useEffect, useState, useRef } from 'react';
import { MinusIcon, PlusIcon, InfoIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
const neuroDescriptions: Record<string, string> = {
  CONTROL: "Measures the player's ability to manage game tempo, maintain composure, and execute plays with precision under varying conditions.",
  ATTENTION: 'Evaluates how effectively the player processes visual and tactical information during live play, filtering distractions.',
  PREDICTION: "Assesses the player's capacity to anticipate opponent actions, read plays before they develop, and position accordingly.",
  RESILIENCE: 'Gauges mental toughness — how well the player recovers from errors, handles pressure, and sustains performance over time.',
  Consistency: "How stable the player's cognitive output remains across games, minimizing variance in decision quality.",
  Discipline: 'The ability to stick to tactical assignments and avoid impulsive decisions that deviate from the game plan.',
  'Play Control': 'Effectiveness in dictating the pace and flow of possessions, managing clock and spacing.',
  Focus: 'Sustained concentration during critical moments, avoiding lapses in awareness during long stretches of play.',
  Selection: 'Quality of shot and pass selection — choosing high-percentage options over forced attempts.',
  Reading: 'Speed and accuracy in interpreting defensive formations and recognizing offensive opportunities.',
  Positioning: 'Ability to occupy optimal court positions before the play develops, both offensively and defensively.',
  Timing: 'Precision in the execution window — arriving at the right spot at the right moment for cuts, rotations, and contests.',
  Interception: 'Anticipation-driven ability to disrupt passing lanes and create turnovers through prediction rather than reaction.',
  Impact: 'How strongly the player influences game outcomes during high-pressure moments and clutch situations.',
  'Error Control': 'Ability to minimize unforced errors and recover quickly when mistakes occur without compounding them.',
  'Load Balance': 'Capacity to maintain cognitive performance across heavy minutes and dense scheduling without degradation.'
};
const PlayerNeuro = () => {
  const [rollingWindow, setRollingWindow] = useState(3);
  const [selectedDot, setSelectedDot] = useState<number | null>(null);
  const [activeInfo, setActiveInfo] = useState<string | null>(null);
  const [openAccordions, setOpenAccordions] = useState<Record<string, boolean>>({
    control: true,
    attention: true,
    prediction: true,
    resilience: true
  });
  const chartRef = useRef<HTMLDivElement>(null);
  const infoRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (chartRef.current && !chartRef.current.contains(e.target as Node)) {
        setSelectedDot(null);
      }
      if (activeInfo && infoRef.current && !infoRef.current.contains(e.target as Node)) {
        setActiveInfo(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [selectedDot, activeInfo]);
  const neuroData = {
    scores: {
      control: 75,
      attention: 82,
      prediction: 91,
      resilience: 88
    },
    optimalUse: 'The player expresses all four cognitive domains at a high level (CTRL 5&6, ATT 65.2, PRED 67.7, RES 64.5). His system is not specialized in a single function — he reads, anticipates, controls and produces with continuity. RESILIENCE is 64.5 and PIR at the 90th percentile indicate that his output does not degrade under heavy load — a player to keep on the court when the game gets tough. OR_perc at the 87th percentile confirms an anticipatory capacity for trajectories that goes beyond physicality.',
    idealScenario: 'In Euroleague the profile is complete and high impact — values drop significantly (OVERALL 80) as cognitive characteristics scale structurally. A player who takes on responsibility in key moments, not as a rhythm manager, but as a cognitive reference point for the team.',
    categories: [{
      id: 'control',
      label: 'CONTROL',
      value: 75,
      level: 'ELITE',
      subcategories: [{
        label: 'Consistency',
        value: 78
      }, {
        label: 'Discipline',
        value: 82
      }, {
        label: 'Play Control',
        value: 65
      }],
      analysis: ['Decides quickly and attacks the first opportunity with personal initiatives and assists. This makes him unpredictable: with so many balls in hand he tends to lose more — the data is consistent with a creator profile, not a rhythm manager.', "In the last twenty games: almost 4 assists per game lost. In away games or on the road, choices drop in quality. Not a mental problem — it's contextual."]
    }, {
      id: 'attention',
      label: 'ATTENTION',
      value: 82,
      level: 'ELITE',
      subcategories: [{
        label: 'Focus',
        value: 85
      }, {
        label: 'Selection',
        value: 79
      }, {
        label: 'Reading',
        value: 82
      }],
      analysis: ['Reads the game well and converts reading into execution points, shoots 84% from the line, serves 5.6 assists in the right spots when to pass.', 'Selection is the most contained sub-domain: mainly from three in transition (25.5%) — varies by context. A creator who attacks when he has space.']
    }, {
      id: 'prediction',
      label: 'PREDICTION',
      value: 91,
      level: 'MODERATE',
      subcategories: [{
        label: 'Positioning',
        value: 94
      }, {
        label: 'Timing',
        value: 88
      }, {
        label: 'Interception',
        value: 91
      }],
      analysis: ['The profile in PREDICTION is strongly polarized. Interception at 91% is a rare data point: he systematically anticipates opponent passing lanes — a cognitive structural signal.', 'Timing and Positioning are solid for structural reasons: stops and rebounds are not just strengths of the role. Not a rim protector nor a rebound anticipator. The area where he can grow the most in the jump to the next level.']
    }, {
      id: 'resilience',
      label: 'RESILIENCE',
      value: 88,
      level: 'ELITE',
      subcategories: [{
        label: 'Impact',
        value: 85
      }, {
        label: 'Error Control',
        value: 92
      }, {
        label: 'Load Balance',
        value: 87
      }],
      analysis: ['ELITE — the sub-domain Impact is the number most linked to the result in a consistent manner — not just under load. It is the cognitive signature of the high-level player.', 'Error Control (92%): maintains low turnovers and fouls at high volume of use (USG 30.8%). Load Balance (87%): elevated at high volume. The margin compared to ELITE percentages in adverse contexts.']
    }]
  };
  const gameReadyData = [{
    date: 'Sep 24',
    value: 4,
    isGame: true,
    opponent: 'vs Celtics',
    cognitiveReadiness: 62
  }, {
    date: '',
    value: 2.8,
    isGame: true,
    opponent: 'vs Heat',
    cognitiveReadiness: 41
  }, {
    date: '',
    value: 4.5,
    isGame: true,
    opponent: '@ Bucks',
    cognitiveReadiness: 58
  }, {
    date: 'Oct 08',
    value: 3.5,
    isGame: true,
    opponent: '@ 76ers',
    cognitiveReadiness: 48
  }, {
    date: '',
    value: 4.8,
    isGame: true,
    opponent: 'vs Knicks',
    cognitiveReadiness: 63
  }, {
    date: '',
    value: 1.5,
    isGame: true,
    opponent: '@ Nets',
    cognitiveReadiness: 28
  }, {
    date: 'Oct 19',
    value: 0.8,
    isGame: true,
    opponent: 'vs Raptors',
    cognitiveReadiness: 18
  }, {
    date: '',
    value: 3.8,
    isGame: true,
    opponent: '@ Bulls',
    cognitiveReadiness: 52
  }, {
    date: '',
    value: 1.2,
    isGame: true,
    opponent: 'vs Pacers',
    cognitiveReadiness: 22
  }, {
    date: 'Oct 30',
    value: 9.5,
    isGame: true,
    opponent: '@ Warriors',
    cognitiveReadiness: 95
  }, {
    date: '',
    value: 3.5,
    isGame: true,
    opponent: 'vs Clippers',
    cognitiveReadiness: 47
  }, {
    date: '',
    value: 1.8,
    isGame: true,
    opponent: '@ Suns',
    cognitiveReadiness: 30
  }, {
    date: 'Nov 13',
    value: 5.5,
    isGame: true,
    opponent: 'vs Nuggets',
    cognitiveReadiness: 68
  }, {
    date: '',
    value: 6.8,
    isGame: true,
    opponent: '@ Thunder',
    cognitiveReadiness: 78
  }, {
    date: '',
    value: 5.2,
    isGame: true,
    opponent: 'vs Mavs',
    cognitiveReadiness: 65
  }, {
    date: 'Dec 07',
    value: 1.8,
    isGame: true,
    opponent: '@ Grizzlies',
    cognitiveReadiness: 29
  }, {
    date: '',
    value: 3.5,
    isGame: true,
    opponent: 'vs Pelicans',
    cognitiveReadiness: 46
  }, {
    date: '',
    value: 7.2,
    isGame: true,
    opponent: '@ Kings',
    cognitiveReadiness: 82
  }, {
    date: 'Dec 21',
    value: 6.5,
    isGame: true,
    opponent: 'vs Wolves',
    cognitiveReadiness: 75
  }, {
    date: '',
    value: 6.2,
    isGame: true,
    opponent: '@ Blazers',
    cognitiveReadiness: 72
  }, {
    date: '',
    value: 6.8,
    isGame: true,
    opponent: 'vs Jazz',
    cognitiveReadiness: 77
  }, {
    date: 'Feb 22',
    value: 7.5,
    isGame: true,
    opponent: '@ Spurs',
    cognitiveReadiness: 84
  }, {
    date: '',
    value: 4.5,
    isGame: true,
    opponent: 'vs Hawks',
    cognitiveReadiness: 58
  }, {
    date: '',
    value: 3.2,
    isGame: true,
    opponent: '@ Wizards',
    cognitiveReadiness: 43
  }, {
    date: '',
    value: 3.8,
    isGame: true,
    opponent: 'vs Hornets',
    cognitiveReadiness: 50
  }, {
    date: '',
    value: 8.5,
    isGame: true,
    opponent: '@ Cavs',
    cognitiveReadiness: 90
  }, {
    date: '',
    value: 7.8,
    isGame: true,
    opponent: 'vs Pistons',
    cognitiveReadiness: 86
  }, {
    date: '',
    value: 3.8,
    isGame: true,
    opponent: '@ Magic',
    cognitiveReadiness: 51
  }, {
    date: '',
    value: 3.8,
    isGame: true,
    opponent: 'vs Rockets',
    cognitiveReadiness: 49
  }];
  // Calculate moving average based on rollingWindow
  const halfWindow = Math.floor(rollingWindow / 2);
  const linePoints = gameReadyData.map((d, i) => {
    const start = Math.max(0, i - halfWindow);
    const end = Math.min(gameReadyData.length - 1, i + halfWindow);
    let sum = 0;
    for (let j = start; j <= end; j++) {
      sum += gameReadyData[j].value;
    }
    return sum / (end - start + 1);
  });
  const getColorClass = (value: number) => {
    if (value >= 80) return 'bg-emerald-600';
    if (value >= 60) return 'bg-amber-600';
    return 'bg-red-600';
  };
  const getDotColor = (value: number) => {
    if (value >= 6) return '#059669'; // emerald-600
    if (value >= 4) return '#d97706'; // amber-600
    return '#dc2626'; // red-600
  };
  // Helper to get display date for a dot (find nearest labeled date)
  const getDisplayDate = (index: number) => {
    // Walk backwards to find the nearest labeled date
    for (let i = index; i >= 0; i--) {
      if (gameReadyData[i].date) {
        const dayOffset = index - i;
        if (dayOffset === 0) return gameReadyData[i].date;
        return `${gameReadyData[i].date} +${dayOffset}`;
      }
    }
    return `Game ${index + 1}`;
  };
  const InfoButton = ({
    label
  }: {
    label: string;
  }) => {
    const isActive = activeInfo === label;
    return <span className="relative inline-flex" ref={isActive ? infoRef : undefined} data-id="element-431">
        <button onClick={e => {
        e.stopPropagation();
        setActiveInfo(isActive ? null : label);
      }} className={`inline-flex items-center justify-center w-3.5 h-3.5 rounded-full text-[10px] font-bold ml-1 transition-colors flex-shrink-0 ${isActive ? 'bg-blue-600 text-white' : 'bg-gray-900 text-white hover:bg-gray-800'}`} data-id="element-432">
          i
        </button>
        {isActive && <div className="absolute z-50 left-1/2 -translate-x-1/2 bottom-full mb-2 w-56 bg-gray-900 text-white rounded-lg shadow-xl px-3.5 py-3 pointer-events-auto" data-id="element-433">
            <div className="text-xs font-bold mb-1.5" data-id="element-434">{label}</div>
            <p className="text-xs text-gray-300 leading-relaxed mb-2.5" data-id="element-435">
              {neuroDescriptions[label] || 'Description coming soon.'}
            </p>
            <button className="text-[10px] uppercase tracking-wider font-bold text-blue-400 hover:text-blue-300 transition-colors" data-id="element-436">
              Read more on our academy →
            </button>
            <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-900" data-id="element-437" />
          </div>}
      </span>;
  };
  const toggleAccordion = (id: string) => {
    setOpenAccordions(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  const getLevelColor = (level: string) => {
    if (level === 'ELITE') return 'text-emerald-600';
    if (level === 'MODERATE') return 'text-amber-600';
    return 'text-red-600';
  };
  return <div className="space-y-6" data-id="element-438">
      {/* Row 1: Radar + Bar Charts + Optimal Use / Ideal Scenario */}
      <div className="bg-white p-6 rounded-lg shadow-sm border" data-id="element-439">
        <div className="flex" data-id="element-440">
          {/* Radar Chart */}
          <div className="w-1/3 flex flex-col items-center justify-center border-r pr-6" data-id="element-441">
            <svg viewBox="0 0 240 240" className="w-full max-w-[240px]" data-id="element-442">
              {/* Grid */}
              {[1, 0.75, 0.5, 0.25].map((scale, i) => <polygon key={i} points={`120,${120 - 90 * scale} ${120 + 90 * scale},120 120,${120 + 90 * scale} ${120 - 90 * scale},120`} fill="none" stroke="#e5e7eb" strokeWidth="1" strokeDasharray={i === 0 ? '0' : '2,2'} data-id="element-443" />)}
              {/* Axes */}
              <line x1="120" y1="30" x2="120" y2="210" stroke="#e5e7eb" strokeWidth="1" data-id="element-444" />
              <line x1="30" y1="120" x2="210" y2="120" stroke="#e5e7eb" strokeWidth="1" data-id="element-445" />

              {/* Data polygon */}
              <polygon points={`120,${120 - 90 * (neuroData.scores.control / 100)} ${120 + 90 * (neuroData.scores.attention / 100)},120 120,${120 + 90 * (neuroData.scores.prediction / 100)} ${120 - 90 * (neuroData.scores.resilience / 100)},120`} fill="rgba(37, 99, 235, 0.15)" stroke="#2563eb" strokeWidth="2" data-id="element-446" />

              {/* Data points */}
              <circle cx="120" cy={120 - 90 * (neuroData.scores.control / 100)} r="4" fill="#2563eb" data-id="element-447" />
              <circle cx={120 + 90 * (neuroData.scores.attention / 100)} cy="120" r="4" fill="#2563eb" data-id="element-448" />
              <circle cx="120" cy={120 + 90 * (neuroData.scores.prediction / 100)} r="4" fill="#2563eb" data-id="element-449" />
              <circle cx={120 - 90 * (neuroData.scores.resilience / 100)} cy="120" r="4" fill="#2563eb" data-id="element-450" />

              {/* Labels */}
              <text x="120" y="20" textAnchor="middle" className="text-[10px] uppercase tracking-wider font-bold fill-gray-500" data-id="element-451">
                CONTROL
              </text>
              <text x="120" y="20" textAnchor="middle" className="text-[10px] font-bold fill-blue-600" dy="12" data-id="element-452">
                {neuroData.scores.control}
              </text>
              <text x="215" y="118" textAnchor="start" className="text-[10px] uppercase tracking-wider font-bold fill-gray-500" data-id="element-453">
                ATTENTION
              </text>
              <text x="215" y="118" textAnchor="start" className="text-[10px] font-bold fill-blue-600" dy="12" data-id="element-454">
                {neuroData.scores.attention}
              </text>
              <text x="120" y="225" textAnchor="middle" className="text-[10px] uppercase tracking-wider font-bold fill-gray-500" data-id="element-455">
                PREDICTION
              </text>
              <text x="120" y="225" textAnchor="middle" className="text-[10px] font-bold fill-blue-600" dy="-12" data-id="element-456">
                {neuroData.scores.prediction}
              </text>
              <text x="25" y="118" textAnchor="end" className="text-[10px] uppercase tracking-wider font-bold fill-gray-500" data-id="element-457">
                RESILIENCE
              </text>
              <text x="25" y="118" textAnchor="end" className="text-[10px] font-bold fill-blue-600" dy="12" data-id="element-458">
                {neuroData.scores.resilience}
              </text>
            </svg>
          </div>

          {/* Parent Bar Charts */}
          <div className="w-2/3 pl-6 flex flex-col justify-center space-y-4" data-id="element-459">
            {neuroData.categories.map(category => <div key={category.id} className="flex items-center" data-id="element-460">
                <div className="w-36 text-xs font-bold text-gray-900 flex items-center" data-id="element-461">
                  {category.label}
                  <InfoButton label={category.label} data-id="element-462" />
                </div>
                <div className="flex-1 flex items-center gap-3" data-id="element-463">
                  <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden" data-id="element-464">
                    <div className={`h-full ${getColorClass(category.value)} transition-all duration-500`} style={{
                  width: `${category.value}%`
                }} data-id="element-465" />
                  </div>
                  <div className="w-10 text-right text-xs font-bold text-gray-900" data-id="element-466">
                    {category.value}%
                  </div>
                </div>
              </div>)}
          </div>
        </div>

        {/* Optimal Use & Ideal Scenario */}
        <div className="flex mt-6 pt-6 border-t" data-id="element-467">
          <div className="flex-1 pr-6" data-id="element-468">
            <h4 className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-2" data-id="element-469">
              Optimal Use
            </h4>
            <p className="text-xs text-gray-600 leading-relaxed" data-id="element-470">
              The player expresses all four cognitive domains at a high level (
              <span className="font-bold text-blue-600" data-id="element-471">CTRL 5&6</span>,{' '}
              <span className="font-bold text-blue-600" data-id="element-472">ATT 65.2</span>,{' '}
              <span className="font-bold text-blue-600" data-id="element-473">PRED 67.7</span>,{' '}
              <span className="font-bold text-blue-600" data-id="element-474">RES 64.5</span>). His
              system is{' '}
              <span className="font-bold text-gray-900" data-id="element-475">
                not specialized in a single function
              </span>{' '}
              — he reads, anticipates, controls and produces with continuity.{' '}
              <span className="font-bold text-emerald-600" data-id="element-476">
                RESILIENCE is 64.5
              </span>{' '}
              and{' '}
              <span className="font-bold text-emerald-600" data-id="element-477">
                PIR at the 90th percentile
              </span>{' '}
              indicate that his output does not degrade under heavy load —{' '}
              <span className="font-bold text-gray-900" data-id="element-478">
                a player to keep on the court when the game gets tough
              </span>
              .{' '}
              <span className="font-bold text-blue-600" data-id="element-479">
                OR_perc at the 87th percentile
              </span>{' '}
              confirms an anticipatory capacity for trajectories that goes
              beyond physicality.
            </p>
          </div>
          <div className="w-px bg-gray-200" data-id="element-480"></div>
          <div className="flex-1 pl-6" data-id="element-481">
            <h4 className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-2" data-id="element-482">
              Ideal Scenario
            </h4>
            <p className="text-xs text-gray-600 leading-relaxed" data-id="element-483">
              In Euroleague the profile is{' '}
              <span className="font-bold text-gray-900" data-id="element-484">
                complete and high impact
              </span>{' '}
              — values drop significantly (
              <span className="font-bold text-blue-600" data-id="element-485">OVERALL 80</span>) as
              cognitive characteristics scale structurally. A player who{' '}
              <span className="font-bold text-gray-900" data-id="element-486">
                takes on responsibility in key moments
              </span>
              , not as a rhythm manager, but as a{' '}
              <span className="font-bold text-emerald-600" data-id="element-487">
                cognitive reference point
              </span>{' '}
              for the team.
            </p>
          </div>
        </div>
      </div>

      {/* Row 2: Subcategory Detail Boxes (2x2 grid with accordion text) */}
      <div className="grid grid-cols-2 gap-4" data-id="element-488">
        {neuroData.categories.map(category => <div key={category.id} className="bg-white p-4 rounded-lg shadow-sm border" data-id="element-489">
            {/* Header with label and level */}
            <div className="flex items-center justify-between mb-3 pb-2 border-b" data-id="element-490">
              <h4 className="text-xs font-bold text-gray-900 flex items-center" data-id="element-491">
                {category.label}
                <InfoButton label={category.label} data-id="element-492" />
              </h4>
              <span className={`text-[10px] uppercase tracking-wider font-bold ${getLevelColor(category.level)}`} data-id="element-493">
                {category.level}
              </span>
            </div>

            {/* Subcategory bars */}
            <div className="space-y-2.5" data-id="element-494">
              {category.subcategories.map((sub, idx) => <div key={idx} className="flex flex-col" data-id="element-495">
                  <div className="flex justify-between items-center text-xs mb-1" data-id="element-496">
                    <span className="font-medium text-gray-600 flex items-center" data-id="element-497">
                      {sub.label}
                      <InfoButton label={sub.label} data-id="element-498" />
                    </span>
                    <span className="font-bold text-gray-900" data-id="element-499">
                      {sub.value}%
                    </span>
                  </div>
                  <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-500">
                    <div className={`h-full ${getColorClass(sub.value)} opacity-80 transition-all duration-500`} style={{
                width: `${sub.value}%`
              }} data-id="element-501" />
                  </div>
                </div>)}
            </div>

            {/* Accordion Analysis Text */}
            <div className="mt-3 pt-3 border-t" data-id="element-502">
              <button onClick={() => toggleAccordion(category.id)} className="flex items-center justify-between w-full text-left" data-id="element-503">
                <span className="text-[10px] uppercase tracking-wider font-bold text-gray-500" data-id="element-504">
                  Analysis
                </span>
                {openAccordions[category.id] ? <ChevronUpIcon className="w-3.5 h-3.5 text-gray-500" data-id="element-505" /> : <ChevronDownIcon className="w-3.5 h-3.5 text-gray-500" data-id="element-506" />}
              </button>
              {openAccordions[category.id] && <div className="mt-2 space-y-2" data-id="element-507">
                  {category.id === 'control' && <>
                      <p className="text-xs text-gray-600 leading-relaxed" data-id="element-508">
                        Decides quickly and attacks the first opportunity with{' '}
                        <span className="font-bold text-gray-900" data-id="element-509">
                          personal initiatives and assists
                        </span>
                        . This makes him{' '}
                        <span className="font-bold text-amber-600" data-id="element-510">
                          unpredictable
                        </span>
                        : with so many balls in hand he tends to lose more — the
                        data is consistent with a{' '}
                        <span className="font-bold text-gray-900" data-id="element-511">
                          creator profile
                        </span>
                        , not a rhythm manager.
                      </p>
                      <p className="text-xs text-gray-600 leading-relaxed" data-id="element-512">
                        In the last twenty games: almost{' '}
                        <span className="font-bold text-blue-600" data-id="element-513">
                          4 assists per game
                        </span>{' '}
                        lost. In away games or on the road, choices drop in
                        quality.{' '}
                        <span className="font-bold text-gray-900" data-id="element-514">
                          Not a mental problem
                        </span>{' '}
                        — it's contextual.
                      </p>
                    </>}
                  {category.id === 'attention' && <>
                      <p className="text-xs text-gray-600 leading-relaxed" data-id="element-515">
                        Reads the game well and converts reading into execution
                        points, shoots{' '}
                        <span className="font-bold text-emerald-600" data-id="element-516">
                          84% from the line
                        </span>
                        , serves{' '}
                        <span className="font-bold text-blue-600" data-id="element-517">
                          5.6 assists
                        </span>{' '}
                        in the right spots when to pass.
                      </p>
                      <p className="text-xs text-gray-600 leading-relaxed" data-id="element-518">
                        <span className="font-bold text-gray-900" data-id="element-519">
                          Selection
                        </span>{' '}
                        is the most contained sub-domain: mainly from three in
                        transition (
                        <span className="font-bold text-amber-600" data-id="element-520">25.5%</span>)
                        — varies by context. A{' '}
                        <span className="font-bold text-gray-900" data-id="element-521">
                          creator who attacks
                        </span>{' '}
                        when he has space.
                      </p>
                    </>}
                  {category.id === 'prediction' && <>
                      <p className="text-xs text-gray-600 leading-relaxed" data-id="element-522">
                        The profile in PREDICTION is{' '}
                        <span className="font-bold text-amber-600" data-id="element-523">
                          strongly polarized
                        </span>
                        .{' '}
                        <span className="font-bold text-gray-900" data-id="element-524">
                          Interception at{' '}
                          <span className="font-bold text-emerald-600" data-id="element-525">
                            91%
                          </span>
                        </span>{' '}
                        is a rare data point: he systematically anticipates
                        opponent passing lanes — a{' '}
                        <span className="font-bold text-blue-600" data-id="element-526">
                          cognitive structural signal
                        </span>
                        .
                      </p>
                      <p className="text-xs text-gray-600 leading-relaxed" data-id="element-527">
                        <span className="font-bold text-gray-900" data-id="element-528">
                          Timing and Positioning
                        </span>{' '}
                        are solid for structural reasons: stops and rebounds are
                        not just strengths of the role. Not a rim protector nor
                        a rebound anticipator.{' '}
                        <span className="font-bold text-amber-600" data-id="element-529">
                          The area where he can grow the most
                        </span>{' '}
                        in the jump to the next level.
                      </p>
                    </>}
                  {category.id === 'resilience' && <>
                      <p className="text-xs text-gray-600 leading-relaxed" data-id="element-530">
                        <span className="font-bold text-emerald-600" data-id="element-531">
                          ELITE
                        </span>{' '}
                        — the sub-domain{' '}
                        <span className="font-bold text-gray-900" data-id="element-532">Impact</span>{' '}
                        is the number most linked to the result in a consistent
                        manner — not just under load. It is the{' '}
                        <span className="font-bold text-blue-600" data-id="element-533">
                          cognitive signature
                        </span>{' '}
                        of the high-level player.
                      </p>
                      <p className="text-xs text-gray-600 leading-relaxed" data-id="element-534">
                        <span className="font-bold text-gray-900" data-id="element-535">
                          Error Control
                        </span>{' '}
                        (<span className="font-bold text-emerald-600" data-id="element-536">92%</span>
                        ): maintains low turnovers and fouls at high volume of
                        use (
                        <span className="font-bold text-blue-600" data-id="element-537">
                          USG 30.8%
                        </span>
                        ).{' '}
                        <span className="font-bold text-gray-900" data-id="element-538">
                          Load Balance
                        </span>{' '}
                        (<span className="font-bold text-emerald-600" data-id="element-539">87%</span>
                        ): elevated at high volume. The margin compared to ELITE
                        percentages in adverse contexts.
                      </p>
                    </>}
                </div>}
            </div>
          </div>)}
      </div>

      {/* Row 3: Game Ready Score Line Chart */}
      <div className="bg-white p-6 rounded-lg shadow-sm border" ref={chartRef} data-id="element-540">
        <div className="flex items-center justify-between mb-6" data-id="element-541">
          <h3 className="text-[10px] uppercase tracking-wider font-bold text-gray-500" data-id="element-542">
            GAME READY SCORE · EVOLUTION (DOTS = INDIVIDUAL GAMES, LINE ={' '}
            {rollingWindow}-GAME ROLLING AVERAGE)
          </h3>
          <div className="flex items-center gap-1.5 flex-shrink-0 ml-4" data-id="element-543">
            <h3 className="text-[10px] uppercase tracking-wider font-bold text-gray-500" data-id="element-544">
              {' '}
              GAMES:{' '}
            </h3>
            <button onClick={() => setRollingWindow(Math.max(2, rollingWindow - 1))} className="w-6 h-6 flex items-center justify-center rounded bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors" data-id="element-545">
              <MinusIcon className="w-3 h-3" data-id="element-546" />
            </button>
            <input type="number" min={2} max={15} value={rollingWindow} onChange={e => {
            const val = parseInt(e.target.value);
            if (!isNaN(val) && val >= 2 && val <= 15) setRollingWindow(val);
          }} className="w-10 h-6 text-center text-xs font-bold border rounded focus:outline-none focus:ring-1 focus:ring-blue-600 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none text-gray-900" data-id="element-547" />
            <button onClick={() => setRollingWindow(Math.min(15, rollingWindow + 1))} className="w-6 h-6 flex items-center justify-center rounded bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors" data-id="element-548">
              <PlusIcon className="w-3 h-3" data-id="element-549" />
            </button>
          </div>
        </div>

        <div className="relative h-48 w-full mt-4" data-id="element-550">
          <svg viewBox="0 0 1000 160" className="w-full h-full" preserveAspectRatio="none" data-id="element-551">
            {/* Background Zones */}
            <rect x="0" y="0" width="1000" height="64" fill="#f0fdf4" fillOpacity="0.5" data-id="element-552" />{' '}
            {/* Green zone > 6 */}
            <rect x="0" y="64" width="1000" height="32" fill="#fffbeb" fillOpacity="0.5" data-id="element-553" />{' '}
            {/* Yellow zone 4-6 */}
            {/* Grid Lines */}
            {[0, 2, 4, 6, 8].map(val => <line key={val} x1="0" y1={160 - val * 16} x2="1000" y2={160 - val * 16} stroke="#e5e7eb" strokeWidth="1" strokeDasharray="4,4" data-id="element-554" />)}
            {/* Y-Axis Labels */}
            {[2, 4, 6, 8].map(val => <text key={val} x="10" y={160 - val * 16 + 4} className="text-[10px] fill-gray-500 font-medium" data-id="element-555">
                {val}
              </text>)}
            {/* X-Axis Labels */}
            {gameReadyData.map((d, i) => d.date && <text key={i} x={30 + i * (940 / (gameReadyData.length - 1))} y="150" textAnchor="middle" className="text-[10px] fill-gray-500 font-medium" data-id="element-556">
                    {d.date}
                  </text>)}
            {/* Rolling Average Line */}
            <polyline points={linePoints.map((val, i) => `${30 + i * (940 / (gameReadyData.length - 1))},${128 - val * 12.8}`).join(' ')} fill="none" stroke="#111827" strokeWidth="2" data-id="element-557" />
            {/* Individual Game Dots */}
            {gameReadyData.map((d, i) => <circle key={i} cx={30 + i * (940 / (gameReadyData.length - 1))} cy={128 - d.value * 12.8} r={selectedDot === i ? 6 : 4} fill={getDotColor(d.value)} opacity={selectedDot === i ? 1 : 0.8} stroke={selectedDot === i ? '#111827' : 'none'} strokeWidth={selectedDot === i ? 2 : 0} className="cursor-pointer transition-all" onClick={() => setSelectedDot(selectedDot === i ? null : i)} data-id="element-558" />)}
          </svg>

          {/* Tooltip */}
          {selectedDot !== null && <div className="absolute z-10 bg-gray-900 text-white rounded-lg shadow-lg px-3 py-2.5 pointer-events-none" style={{
          left: `${(30 + selectedDot * (940 / (gameReadyData.length - 1))) / 1000 * 100}%`,
          top: `${(128 - gameReadyData[selectedDot].value * 12.8) / 160 * 100 - 8}%`,
          transform: 'translate(-50%, -100%)'
        }} data-id="element-559">
              <div className="text-[10px] text-gray-400 font-medium mb-0.5" data-id="element-560">
                {getDisplayDate(selectedDot)}
              </div>
              <div className="text-xs font-bold" data-id="element-561">
                {gameReadyData[selectedDot].opponent}
              </div>
              <div className="flex items-center gap-1.5 mt-1" data-id="element-562">
                <span className="text-[10px] text-gray-400" data-id="element-563">
                  Cognitive Readiness
                </span>
                <span className="text-xs font-bold" style={{
              color: getDotColor(gameReadyData[selectedDot].value)
            }} data-id="element-564">
                  {gameReadyData[selectedDot].cognitiveReadiness}%
                </span>
              </div>
              <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-900" data-id="element-565" />
            </div>}
        </div>
      </div>
    </div>;
};
export default PlayerNeuro;